import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { adminApiClient, User } from '../lib/api';
// import { API_CONFIG } from '../lib/constants';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token and validate it
    const validateToken = async () => {
      try {
        const token = localStorage.getItem('admin_auth_token');

        if (token) {
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
          const response = await fetch(`${API_URL}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          const data = await response.json();

          if (response.ok && data.success && data.data && data.data.user) {
            setUser(data.data.user);
          } else {
            localStorage.removeItem('admin_auth_token');
          }
        }
      } catch (error) {
        localStorage.removeItem('admin_auth_token');
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Use API client for password login
      const response = await adminApiClient.login({ email, password });

      if (response.success && response.data?.token) {
        const token = response.data.token;
        const user = response.data.user;

        localStorage.setItem('admin_auth_token', token);

        // Set user from response if available
        if (user) {
          setUser(user);
        } else {
          // Fallback: Get user profile if user data not in response
          // Note: We don't set global loading here to avoid UI flickering
          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002/api';
          const profileResponse = await fetch(`${API_URL}/auth/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          const profileData = await profileResponse.json();

          if (profileData.success && profileData.data && profileData.data.user) {
            setUser(profileData.data.user);
          } else {
            throw new Error('Failed to get user profile');
          }
        }
      } else {
        throw new Error(response.error || response.message || 'Invalid email or password');
      }
    } catch (error: any) {
      // Remove any invalid token
      localStorage.removeItem('admin_auth_token');

      // Re-throw the original error so components can handle specific validation details
      throw error;
    }
  };

  const updateProfile = async (userData: Partial<User>) => {
    try {
      setLoading(true);
      const response = await adminApiClient.updateProfile(userData);

      if (response.success && response.data) {
        setUser(response.data.user);
      } else {
        throw new Error(response.error || 'Profile update failed');
      }
    } catch (error: any) {
      // Profile update error handled silently
      throw new Error(error.response?.data?.error || error.message || 'Profile update failed');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    adminApiClient.logout();
  };

  const value = {
    user,
    loading,
    login,
    logout,
    updateProfile,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}